<template>
  <div class="station-monitor">
    <!-- 主内容区域 -->
    <div class="main-content" :class="{
      'main-content--scrolled': isScrolled,
      'main-content--no-stats': noStats
    }">
      <!-- Tab导航栏 - 固定不滚动 -->
      <div class="tab-nav-wrapper">
        <TabNav v-model="activeTab" :tabs="tabs" @update:modelValue="handleTabChange" />
        <div class="tab-nav-extra" id="tab-nav-extra"></div>
      </div>

      <!-- 可滚动内容区域 -->
      <div class="scrollable-content" :class="{
        'scrollable-content--enter': isEnter
      }">
        <component v-if="currentComponent" :is="currentComponent" :base-data="stationBaseData" :elink-data="elinkData"
          :loading-elink="loadingElink" :power-tracking-data="powerTrackingData"></component>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, provide, watch, inject, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getElinkList, fetchPowerTrackingData } from '@/api/station'
import useAppStore from '@/store/modules/app'

/* components */
import TabNav from './components/TabNav.vue'
import RealTimeMonitor from './tabs/RealTimeMonitor.vue'
import OverView from './tabs/OverView.vue'
import ElecTrovalency from './tabs/ElecTrovalency.vue'
import Statement from './tabs/Statement.vue'
import AlertManagement from './tabs/AlertManagement.vue'
import SmartAlert from './tabs/SmartAlert.vue'
import StrategyController from './tabs/StrategyController/index.vue'
import ChargeRecord from './tabs/ChargeRecord/index.vue'
import MeterData from './tabs/MeterData.vue'
import EblockSetting from './tabs/EblockSetting/index.vue'
import StationTopo from './tabs/StationTopo/index.vue'
import EnergyStorageSystem from './tabs/EnergyStorageSystem/index.vue'
import ElecPriceMaintain from './tabs/elecPriceMaintain/index.vue'
import RemoteMeterData from './tabs/meterSetting/index.vue'
import Meter from './tabs/Meter/index.vue'
import PvMonitor from './tabs/PvMonitor/index.vue'
import DataAnalysis from './components/data-analysis/index.vue'
import StrategyTemplate from '@/views/business-operations/strategy-template/index.vue'
import WeatherData from '@/views/business-operations/weather-data/index.vue'
import AdvancedConfig from './tabs/advancedConfig/index.vue'
import RemotePower from './tabs/RemotePower/index.vue'

const Templates = {
  '主监控': RealTimeMonitor,
  '储能系统': EnergyStorageSystem,
  '告警管理': AlertManagement,
  '告警信息': AlertManagement,
  '智能告警': SmartAlert,
  '策略控制': StrategyController,
  '充放记录': ChargeRecord,
  '电表读数': MeterData,
  '收益总览': OverView,
  '电价录入': ElecTrovalency,
  '抄表结算': Statement,
  '高级策略配置': EblockSetting,
  '电站拓扑': StationTopo,
  '电价维护': ElecPriceMaintain,
  '远程电表': RemoteMeterData,
  '电表': Meter,
  '光伏': PvMonitor,
  '数据分析': DataAnalysis,
  '策略模板': StrategyTemplate,
  '天气数据': WeatherData,
  '高级配置': AdvancedConfig,
  '远程开关机': RemotePower,
}

const currentComponent = computed(() => {
  return Templates[activeTab.value]
})


// 埋点相关
const trackEvent = inject('trackEvent', () => { })
const trackEventTypes = inject('trackEventTypes', {
  clickIntoPage: 'ClickIntoPage'
})

const props = defineProps({
  tabs: {
    type: Array,
    default: () => [
      '主监控',
      '电站拓扑',
      '储能系统',
      "充放记录",
      "电表",
      "光伏",
    ]
  }
})

// Data fetching - 需要先初始化 route 和 router
const route = useRoute()
const router = useRouter()

// 滚动状态
const isScrolled = ref(true)
const scrollContainer = ref([])
const noStats = ref(true)

const isEnter = ref(false)

// 监听滚动事件
const handleScroll = (e) => {
  const scrollTop = e.target.scrollTop
  // 当滚动超过 10px 时，触发吸顶效果
  // isScrolled.value = scrollTop > 10
}
const appStore = useAppStore()
// stationId 兜底链：URL query → store（持久化） → 电站列表首个
// 统一归一化为字符串，避免与 route.query 比较时因类型不同产生误判
const stationId = computed(() => {
  const id = route.query.stationId || appStore.stationId || appStore.stations?.[0]?.stationId
  return id != null && id !== '' ? String(id) : ''
})

// 当前激活的Tab
const getInitialTab = () => {
  // 首先从路由参数获取
  if (route.query.tab && typeof route.query.tab === 'string') {
    return route.query.tab
  }
  // 然后从sessionStorage获取
  const savedTab = sessionStorage.getItem(`stationTab`)
  if (savedTab && props.tabs.includes(savedTab)) {
    return savedTab
  }
  // 最后使用默认值
  return props.tabs[0] ?? '主监控'
}

const activeTab = ref(getInitialTab())
const stationBaseData = computed(() => appStore.stationBaseData)
const powerTrackingData = ref([]) // 功率跟踪数据

const loadingElink = ref(false)
let wsTimer = null

const handleTabChange = (newTab) => {
  // 切换tab时，更新路由参数   TODO: 更换时，会触发页面刷新
  router.replace({ query: { ...route.query, tab: newTab } })
}

// 同步 stationId 到 appStore
watch(() => stationId.value, async (newId) => {
  // 冷启动/清缓存时 stations 尚未就绪，兜底链会落空，先拉取列表再取一次
  if (!newId && !appStore.stations?.length) {
    await appStore.getStations()
    newId = stationId.value
  }
  // 仍拿不到电站（无权限/列表为空）：不发请求，避免 /eLinkList/NaN
  if (!newId) return

  // URL 缺少 stationId 时回填。Navbar 的 hasStationId() 与 provide 均以 query 为准，
  // 不回填则顶部站级头部不渲染、子组件 inject 到空值
  if (route.query.stationId !== newId) {
    router.replace({ query: { ...route.query, stationId: newId } })
  }

  if (String(appStore.stationId) !== newId) {
    appStore.setStationId(newId)
  }
  fetchElinkData()
}, { immediate: true })

// 监听 tab 切换，添加埋点并保存到sessionStorage
watch(() => activeTab.value, (newTab, oldTab) => {
  // 切换 tab 时滚动容器不会重建，需要手动回到顶部，
  // 否则再次进入某个 tab 时会沿用上次的滚动位置
  nextTick(() => {
    const el = document.querySelector('.scrollable-content')
    if (el) el.scrollTop = 0
  })
  if (oldTab && newTab !== oldTab) {
    // 保存到sessionStorage
    sessionStorage.setItem(`stationTab`, newTab)

    const trackData = {
      targetId: '',
      refData: {
        targetName: `电站监控/Tab切换："From${oldTab}"To"${newTab}"`,
        desc: `Tab切换："${oldTab}"到"${newTab}"`,
      }
    }
    trackEvent(trackEventTypes.clickIntoPage, trackData)
  }
})

// 使用 provide 向所有子孙组件提供电站信息
// 使用 computed 确保响应式更新
provide('stationId', stationId)
provide('stationBaseData', computed(() => stationBaseData.value))
provide('stationName', computed(() => stationBaseData.value?.name || ''))

// eLink 和 eBlock 数据（用于获取功率跟踪数据）
const elinkData = ref([])
const elinkIDs = computed(() => {
  return elinkData.value.map(item => item.uuid).filter(Boolean)
})
const eblockIDs = computed(() => {
  const blocks = []
  elinkData.value.forEach(elink => {
    if (elink.children && Array.isArray(elink.children)) {
      elink.children.forEach(eblock => {
        if (eblock.uuid) {
          blocks.push(eblock.uuid)
        }
      })
    }
  })
  return blocks
})

// 获取 eLink 列表数据
async function fetchElinkData() {
  loadingElink.value = true
  try {
    const res = await getElinkList(stationId.value)
    if (res.code === 200 && Array.isArray(res.data)) {
      elinkData.value = res.data.map(item => ({
        equipmentId: item.equipmentId,
        equipmentName: item.equipmentName,
        uuid: item.uuid,
        equipmentSN: item.equipmentSN,
        children: item.list || []
      }))
      // 获取到 elink 数据后，立即获取功率跟踪数据
      await fetchPowerTracking()
    }
  } catch (error) {
    console.error('Failed to fetch elink list:', error)
  } finally {
    loadingElink.value = false
  }
}

// 获取功率跟踪数据
async function fetchPowerTracking() {
  if (elinkIDs.value.length === 0 && eblockIDs.value.length === 0) {
    console.warn('No elink/eblock IDs available for power tracking')
    return
  }

  try {
    const today = new Date()
    const startTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0)
    const endTime = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59)

    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }

    const response = await fetchPowerTrackingData({
      startTime: formatDate(startTime),
      endTime: formatDate(endTime),
      stationId: stationId.value,
      allElink: true,
      allEblock: true,
      elinkIDs: elinkIDs.value,
      eblockIDs: eblockIDs.value
    })

    if (response && response.data) {
      powerTrackingData.value = response.data
    }
  } catch (error) {
    console.error('Failed to fetch power tracking data:', error)
  }
}

// 监听路由 query 中的 tab 参数变化
watch(() => route.query.tab, (newTab) => {
  if (newTab && typeof newTab === 'string') {
    activeTab.value = newTab
    // 当通过URL修改tab时，也保存到sessionStorage
    sessionStorage.setItem(`stationTab`, newTab)
  }
}, { immediate: true })

onMounted(() => {
  scrollContainer.value = document.querySelector('.scrollable-content')
  if (scrollContainer.value) {
    scrollContainer.value.addEventListener('scroll', handleScroll)
  }

  // 如果 URL 中有 tab 参数，设置 activeTab
  if (route.query.tab && typeof route.query.tab === 'string') {
    activeTab.value = route.query.tab
  }
})

onUnmounted(() => {
  if (scrollContainer.value) {
    scrollContainer.value.removeEventListener('scroll', handleScroll)
  }
  clearTimeout(wsTimer)
})
</script>

<style scoped lang="scss">
.station-monitor {
  padding: 0 28px 28px 0;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  max-width: 100%;
  animation: zoom 0.5s ease;
}

@keyframes zoom {
  from {
    transform: scale(0.95);
  }

  to {
    transform: scale(1);
  }
}

.header-section {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: -106px;
  flex-shrink: 0;
  z-index: 10;
  width: 100%;
  transition: all 0.3s ease;

  &--fixed {
    margin-bottom: 0;
  }

  &--no-stats {
    margin-bottom: 0;
  }
}

.main-content {
  padding: 16px 0 0 28px;
  flex: 1;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.63) 0%, rgba(255, 255, 255, 0.42) 100.75%);
  border-radius: 30px;
  border: 0.5px solid rgba(255, 255, 255, 1);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  transition: margin-top 0.3s ease;

  &--scrolled {
    margin-top: 22px;
  }

  &--no-stats {
    margin-top: 16px;
  }
}

.tab-nav-wrapper {
  flex-shrink: 0;
  padding-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
}

.tab-nav-extra {
  margin-right: 16px;
  position: absolute;
  right: 0;
}

.scrollable-content {
  flex: 1;
  min-height: 0;
  min-width: 0;
  max-width: 100%;
  overflow-y: auto;
  margin-bottom: 28px;
  padding-right: 28px;
  overflow-x: hidden;
}

.scrollable-content--enter {
  overflow-y: hidden;
}

@media (max-width: 1366px) {
  .station-monitor {
    padding: 0 16px 20px 0;
  }

  .main-content {
    padding-left: 16px;
  }

  .scrollable-content {
    margin-bottom: 20px;
    padding-right: 16px;
  }

  .tab-nav-wrapper {
    padding-bottom: 16px;
  }
}

@media (max-width: 768px) {
  .station-monitor {
    padding-right: 8px;
    padding-bottom: 12px;
  }

  .main-content {
    padding-left: 12px;
    border-radius: 20px;
  }

  .scrollable-content {
    margin-bottom: 12px;
    padding-right: 12px;
  }
}

/* 切换时的动画 */
.fade-transform-leave-active,
.fade-transform-enter-active {
  transition: all .4s;
}

.fade-transform-enter {
  opacity: 0;
  transform: translateX(30px);
}

.fade-transform-leave-to {
  opacity: 0;
  transform: translateX(-30px);
}
</style>
